import type { ToolCall } from '@kanbu/schema';
import { pendingFormOf } from '@kanbu/schema/contracts';
import {
  MessageGenerationStatus,
  SourcesDisplayMode,
  SourcesFormat,
} from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans } from '@lingui/react/macro';
import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { memo, useContext, useMemo } from 'react';

import { FollowUpHints } from './follow-up-hints';
import { type SharedMessageProps, Message } from './message';
import {
  InterruptedNotice,
  MessageBlocks,
  messageHasVisibleContent,
  messageHasAnswerContent,
} from './message-blocks';
import { UserAvatar } from './user-avatar';
import { useTimeAgo } from '../../hooks';
import type { RenderUnknownTool, ToolRegistry } from '../../tools';
import type { ChatMessageItem, MessagesGroup } from '../../types';
import { ChatRendererContext } from '../chat-renderer/chat-renderer-provider';

export interface AssistantMessageProps extends SharedMessageProps {
  name?: string;
  avatar?: string;
  toolRegistry?: ToolRegistry;
  renderUnknownTool?: RenderUnknownTool;
  showToolActivity?: boolean;
  expandToolActivity?: boolean;
  showReasoning?: boolean;
  debugToolCalls?: boolean;
  renderTrailStepInspector?: (toolCall: ToolCall) => React.ReactNode;
  renderSources?: (message: ChatMessageItem) => React.ReactNode;
  sourcesFormat?: SourcesFormat;
  sourcesDisplayMode?: SourcesDisplayMode;
  inlineCitations?: boolean;
  isFetching?: boolean;
  isActive?: boolean;
  onRetry?: () => void;
  shouldRenderFeedback?: (group: MessagesGroup) => boolean;
  FeedbackComponent?: React.ComponentType<{
    message: ChatMessageItem;
  }>;
}

export const AssistantMessage = memo(
  ({
    group,
    name,
    avatar,
    toolRegistry,
    renderUnknownTool,
    showToolActivity = false,
    expandToolActivity = false,
    showReasoning = false,
    debugToolCalls = false,
    renderTrailStepInspector,
    renderSources,
    sourcesFormat = SourcesFormat.Simple,
    sourcesDisplayMode = SourcesDisplayMode.Pills,
    inlineCitations = false,
    FeedbackComponent,
    shouldRenderFeedback,
    isFetching,
    isActive,
    onRetry,
    position = 'left',
    variant = 'secondary',
  }: AssistantMessageProps) => {
    const { messages, createdAt, isLast, isInitial } = group;
    const timeAgo = useTimeAgo(createdAt);
    const firstMessage = messages.at(-1);
    /**
     * Gated by the same display config the renderer uses, so the typing
     * indicator stays up while the turn is only reasoning or running a
     * hidden tool instead of blinking out to an empty bubble.
     */
    const hasContent = useMemo(
      () =>
        group.messages.some(message =>
          messageHasVisibleContent(message, {
            showReasoning,
            showToolActivity,
            debugToolCalls,
          }),
        ),
      [group.messages, showReasoning, showToolActivity, debugToolCalls],
    );

    const onFollowUpSelect = useContext(ChatRendererContext)?.append;
    const lastMessage = messages.at(-1);
    const feedbackMessage =
      messages.findLast(messageHasAnswerContent) ?? lastMessage;
    const feedback = (
      <AnimatePresence mode='wait'>
        {FeedbackComponent &&
          firstMessage &&
          !(isFetching && isLast) &&
          (shouldRenderFeedback?.(group) ?? true) && (
            <motion.div
              className='w-full'
              initial={
                !isFetching && !isInitial && isLast
                  ? { opacity: 0, y: 2 }
                  : false
              }
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.2,
                duration: 0.2,
              }}
            >
              <FeedbackComponent message={firstMessage} />
            </motion.div>
          )}
      </AnimatePresence>
    );
    const followUpQuestions = lastMessage?.followUpQuestions ?? [];
    const hasPendingForm = group.messages.some(
      message => pendingFormOf(message) !== null,
    );
    const hasTerminatedAnswer = group.messages.some(
      message =>
        message.generationStatus === MessageGenerationStatus.Stopped ||
        message.generationStatus === MessageGenerationStatus.Failed,
    );
    const showFollowUps =
      isLast &&
      !isFetching &&
      followUpQuestions.length > 0 &&
      lastMessage?.answered !== false &&
      !hasPendingForm &&
      !hasTerminatedAnswer;

    return (
      <Message.Root variant={variant} position={position}>
        <Message.Header className='flex items-center gap-2'>
          <Message.Avatar>
            <UserAvatar src={avatar} fallback={formatUtils.initials(name)} />
          </Message.Avatar>
          <span>{name}</span>
          <AnimatePresence>
            {isActive && (
              <motion.span
                className='text-xs text-gray-500'
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <Loader2 className='animate-spin size-3' strokeWidth={3} />
              </motion.span>
            )}
          </AnimatePresence>
        </Message.Header>

        <Message.Wrapper className='max-w-full'>
          <Message.Content>
            {hasContent ? (
              messages.map(message => {
                const messageRetry =
                  isLast && message.id === lastMessage?.id
                    ? onRetry
                    : undefined;

                return (
                  <div
                    key={message.id}
                    className='flex w-full min-w-0 flex-col gap-1'
                  >
                    <MessageBlocks
                      message={message}
                      toolRegistry={toolRegistry}
                      renderUnknownTool={renderUnknownTool}
                      showToolActivity={showToolActivity}
                      expandToolActivity={expandToolActivity}
                      showReasoning={showReasoning}
                      debugToolCalls={debugToolCalls}
                      renderTrailStepInspector={renderTrailStepInspector}
                      renderSources={renderSources}
                      sourcesFormat={sourcesFormat}
                      sourcesDisplayMode={sourcesDisplayMode}
                      inlineCitations={inlineCitations}
                      answerFooter={
                        message.id === feedbackMessage?.id
                          ? feedback
                          : undefined
                      }
                    />

                    {message.generationStatus ===
                    MessageGenerationStatus.Stopped ? (
                      <InterruptedNotice
                        mode='stopped'
                        onRetry={messageRetry}
                      />
                    ) : message.generationStatus ===
                      MessageGenerationStatus.Failed ? (
                      <InterruptedNotice mode='failed' onRetry={messageRetry} />
                    ) : message.status === 'interrupted' ? (
                      <InterruptedNotice
                        mode='stopped'
                        onRetry={messageRetry}
                      />
                    ) : null}
                  </div>
                );
              })
            ) : (
              <Message.BubbleGroup>
                {isActive ? (
                  <Message.Typing />
                ) : (
                  <Message.Bubble>
                    <Trans>
                      The chat is currently unavailable. Please try again later.
                    </Trans>
                  </Message.Bubble>
                )}
              </Message.BubbleGroup>
            )}

            <Message.Footer>{timeAgo}</Message.Footer>

            {showFollowUps && (
              <FollowUpHints
                questions={followUpQuestions}
                onSelect={onFollowUpSelect}
              />
            )}
          </Message.Content>
        </Message.Wrapper>
      </Message.Root>
    );
  },
);

export {
  messageHasRenderableBlocks,
  messageHasVisibleContent,
  messageHasAnswerContent,
} from './message-blocks';
